import { FC } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ArrowLeft, Dices } from 'lucide-react';
import { isNullish } from 'remeda';

import Button from '@mui/material/Button';

import { Activity } from './activity/Activity';
import { FavoriteSlots } from './favoriteSlots/FavoriteSlots';
import { InvitedBy } from './invitedBy/InvitedBy';
import { PointsAndVip } from './pointsAndVip/PointsAndVip';
import { ProfileHeader } from './profileHeader/ProfileHeader';
import { Referrals } from './referrals/Referrals';
import { Rewards } from './rewards/Rewards';
import { SameFingerprintUsers } from './sameFingerprintUsers/SameFingerprintUsers';
import { SameIpUsers } from './sameIpUsers/SameIpUsers';
import { TournamentHistory } from './tournamentHistory/TournamentHistory';
import { UserComment } from './userComment/UserComment';

import css from './UserProfilePage.module.css';

import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { useUserProfileData } from 'v2/hooks/api/useUserProfileData';
import { AppRoute } from 'v2/routes';

export const UserProfilePage: FC = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user, stats, fingerprints, balance, activeTickets, slots, inviter, vipLevels, levels, isLoading } =
    useUserProfileData(userId);

  if (isLoading) {
    return (
      <PageLayout>
        <Loader />
      </PageLayout>
    );
  }

  if (isNullish(user)) {
    return (
      <PageLayout className={css.userProfilePage}>
        <Button
          className={css.backButton}
          startIcon={<ArrowLeft width={18} height={18} />}
          onClick={() => navigate(-1)}
          size="small"
        >
          Back to Users
        </Button>

        <EmptyPlaceholder text="User not found" />
      </PageLayout>
    );
  }

  return (
    <PageLayout className={css.userProfilePage}>
      <div className={css.topActions}>
        <Button
          className={css.backButton}
          startIcon={<ArrowLeft width={18} height={18} />}
          onClick={() => navigate(-1)}
          size="small"
        >
          Back to Users
        </Button>

        <Button
          component={Link}
          to={`${AppRoute.PROVIDER_TRANSACTIONS_PAGE}?userId=${user.id}`}
          startIcon={<Dices width={18} height={18} />}
          variant="outlined"
          size="small"
        >
          Spins
        </Button>
      </div>

      <div className={css.grid}>
        <ProfileHeader fingerprints={fingerprints} stats={stats} user={user} />

        <UserComment userId={user.id} />

        <SameIpUsers ip={user.clientIP} userId={user.id} />

        <SameFingerprintUsers fingerprints={fingerprints ?? []} userId={user.id} />

        <InvitedBy hasInviter={user.inviterId != null} inviter={inviter} />

        <Referrals userId={user.id} />

        <PointsAndVip
          balance={balance}
          activeTickets={activeTickets}
          user={user}
          vipLevels={vipLevels}
          levels={levels}
        />

        <Activity stats={stats} user={user} />
      </div>

      <TournamentHistory stats={stats} userId={user.id} />

      <Rewards userId={user.id} />

      <FavoriteSlots slots={slots} />
    </PageLayout>
  );
};
